import React, { useRef } from 'react';
import { BottomSheet, HANDLE_AREA_HEIGHT } from '../bottom-sheet';
import { DayPickerOverlay } from './DayPickerOverlay';
import { Stack } from '../stack';
import { useScrollMask } from './hooks/useScrollMask';
import { useDatePickerAttributes } from './hooks/useDatePickerAttributes';
import { buildCSSWithLength, getZIndex } from '../../styles';
import { css } from '@emotion/react';
import {
  bottom_sheet_picker_css,
  date_picker_base_css,
  date_picker_compact_content_css,
  date_picker_compact_footer_css,
  date_picker_compact_right_css,
  popover_picker_css,
} from './styles';
import { DatePickerMask } from './DatePickerMask';
import { DisplayMode } from './types';

export interface DatePickerDisplayProps {
  /**
   * 달력이 노출되는 모드를 지정할 수 있습니다.
   *   - 'bottom-sheet'일 경우 Footer 영역(취소/확인)은 고정 노출됩니다.
   * @default 'popover'
   */
  displayMode?: DisplayMode;
  className?: string;
  disabled?: boolean;
  /** @default getZIndex('picker') */
  zIndex?: number;
  input: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  content: React.ReactNode;
  footer: React.ReactNode;
  compact?: boolean;
  opened: boolean;
  onOpen: () => void;
  onClose: () => void;
  onDismiss: () => void;
}

export const DatePickerDisplay: React.FC<DatePickerDisplayProps> = ({
  displayMode = 'popover',
  className,
  disabled,
  zIndex = getZIndex('picker'),
  input,
  leftContent,
  rightContent,
  content,
  footer,
  compact,
  opened,
  onOpen,
  onClose,
  onDismiss,
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  const { mask, maskPosition, handleScroll } = useScrollMask({
    opened: Boolean(opened),
    calendarRef,
    scrollWrapperRef,
  });
  const { maxHeight } = useDatePickerAttributes(displayMode);

  switch (displayMode) {
    case 'bottom-sheet':
      return (
        <>
          {input}
          <BottomSheet
            open={opened}
            onClose={() => {
              onClose();
              onDismiss();
            }}
            shouldCloseOnDrag={false}
            contentProps={{ p: '0', maxHeight }}
          >
            {() => (
              <Stack
                direction='column'
                css={[
                  date_picker_base_css,
                  css`
                    ${buildCSSWithLength('max-height', maxHeight - HANDLE_AREA_HEIGHT)};
                  `,
                ]}
                ref={scrollWrapperRef}
              >
                <DatePickerMask style={{ top: maskPosition.top, opacity: mask.topMask }} position='top' />
                <DatePickerMask style={{ bottom: maskPosition.bottom, opacity: mask.bottomMask }} position='bottom' />
                <Stack
                  direction='column'
                  align='center'
                  gap={16}
                  css={bottom_sheet_picker_css}
                  onScroll={handleScroll}
                  ref={calendarRef}
                >
                  {content}
                </Stack>
                {footer}
              </Stack>
            )}
          </BottomSheet>
        </>
      );
    case 'calendar':
      return content;
    case 'popover':
    default:
      return (
        <DayPickerOverlay
          opened={opened}
          setOpened={(value) => {
            if (value) {
              onOpen();
            } else {
              onClose();
            }
          }}
          onCloseOutsidePress={() => onDismiss()}
          className={className}
          disabled={disabled}
          zIndex={zIndex}
          content={() => (
            <Stack align='stretch' css={[date_picker_base_css, popover_picker_css]}>
              {leftContent}
              {compact ? (
                <Stack direction='column' align='flex-end'>
                  <Stack align='stretch'>
                    <div css={date_picker_compact_content_css}>{content}</div>
                    <div css={date_picker_compact_right_css}>{rightContent}</div>
                  </Stack>
                  <div css={date_picker_compact_footer_css}>{footer}</div>
                </Stack>
              ) : (
                <Stack direction='column' gap={20} p={20} align='flex-end'>
                  <Stack align='stretch'>
                    {content}
                    {rightContent}
                  </Stack>
                  {footer}
                </Stack>
              )}
            </Stack>
          )}
        >
          {input}
        </DayPickerOverlay>
      );
  }
};
